import { useEffect, useState } from "react";
import { apiClient } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  TrendingUp,
  Users,
  PoundSterling,
  CalendarRange,
  XCircle,
  Sparkles,
} from "lucide-react";

const fmtGBP = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(n || 0);

const CONF_STYLES = {
  high: { label: "High confidence", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  medium: { label: "Medium confidence", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  low: { label: "Low confidence", cls: "bg-rose-100 text-rose-700 border-rose-200" },
};

function Stat({ icon: Icon, label, value, sub, tone = "slate", testId }) {
  const tones = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
    indigo: "text-indigo-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-500">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className={`mt-0.5 text-xl font-semibold ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function ForecastPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .get("/forecast")
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || "Failed to load forecast");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const conf = data?.confidence ? CONF_STYLES[data.confidence] : null;

  return (
    <Card className="border-slate-200" data-testid="forecast-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-500" />
            Forecast
          </CardTitle>
          {conf && (
            <Badge className={`border ${conf.cls}`} data-testid="forecast-confidence">
              {conf.label}
            </Badge>
          )}
        </div>
        {data && (
          <p className="text-xs text-slate-500 mt-1">
            Based on {data.basis?.avgDailyLast28 ?? 0} avg signups/day over the last {data.lookbackDays || 28} days · momentum {Math.round((data.adjustedMomentum || 1) * 100)}%
          </p>
        )}
      </CardHeader>
      <CardContent>
        {loading && !data && (
          <div className="text-center py-6 text-slate-500 text-sm" data-testid="forecast-loading">
            Loading forecast...
          </div>
        )}
        {error && !data && (
          <div className="text-sm text-rose-700" data-testid="forecast-error">
            {error}
          </div>
        )}
        {data && (
          <div className="space-y-4">
            {/* Next 7 days */}
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5">
                <CalendarRange className="h-3.5 w-3.5" /> Next 7 days
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="forecast-next7">
                <Stat
                  icon={TrendingUp}
                  label="Expected conversions"
                  value={data.next7.expectedConversions}
                  tone="emerald"
                  testId="forecast-7-conversions"
                />
                <Stat
                  icon={PoundSterling}
                  label="Expected revenue"
                  value={fmtGBP(data.next7.expectedRevenue)}
                  tone="amber"
                  testId="forecast-7-revenue"
                />
                <Stat
                  icon={Users}
                  label="Fundraisers needed"
                  value={data.next7.fundraisersNeeded}
                  sub={`Scheduled: ${data.next7.scheduledFundraisers}`}
                  tone="indigo"
                  testId="forecast-7-fundraisers"
                />
              </div>
            </div>

            {/* Next 30 days */}
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5">
                <CalendarRange className="h-3.5 w-3.5" /> Next 30 days
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="forecast-next30">
                <Stat
                  icon={TrendingUp}
                  label="Projected conversions"
                  value={data.next30.projectedConversions}
                  tone="emerald"
                  testId="forecast-30-conversions"
                />
                <Stat
                  icon={PoundSterling}
                  label="Projected revenue"
                  value={fmtGBP(data.next30.projectedRevenue)}
                  tone="amber"
                  testId="forecast-30-revenue"
                />
                <Stat
                  icon={XCircle}
                  label="Projected cancellations"
                  value={data.next30.projectedCancellations}
                  tone="rose"
                  testId="forecast-30-cancellations"
                />
              </div>
            </div>

            {/* Trend chart — 30 day expected line */}
            <div data-testid="forecast-chart">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                Daily outlook (30 days)
              </div>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.next30.daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => d?.slice(5)}
                      tick={{ fontSize: 10, fill: "#64748b" }}
                      interval={3}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748b" }} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="expected"
                      stroke="#6366f1"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <p className="text-[11px] text-slate-400">
              Conservative estimates. Growth momentum is damped; declines are applied in full.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
